import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaFacebookF, FaXTwitter, FaInstagram, FaLinkedinIn } from 'react-icons/fa6';

const SuccessPopup = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    className="fixed inset-0 z-50 flex items-center justify-center px-4"
  >
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.4 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 bg-black"
      onClick={onClose}
    />
    <div className="relative bg-white text-black p-8 rounded-lg max-w-md w-full">
      <div className="text-center">
        <div className="mb-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="w-16 h-16 bg-green-500 rounded-full mx-auto flex items-center justify-center"
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </motion.div>
        </div>
        <h3 className="text-2xl font-semibold mb-2">Thank you!</h3>
        <p className="text-gray-600 mb-6">Your message has been sent successfully. We'll get back to you soon.</p>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onClose}
          className="bg-black text-white px-6 py-2 rounded-lg font-medium"
        >
          Close
        </motion.button>
      </div>
    </div>
  </motion.div>
);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const socialLinks = [
    /* { icon: FaFacebookF, href: '#facebook', label: 'Facebook' }, */
    { icon: FaXTwitter, href: '#twitter', label: 'X (Twitter)' },
    { icon: FaInstagram, href: 'https://www.instagram.com/bookjacks?igsh=Y3RldXRwYWl2eHg0', label: 'Instagram' },
    { icon: FaLinkedinIn, href: '#linkedin', label: 'LinkedIn' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, error: null });

    try {
      const response = await fetch('https://formsubmit.co/ajax/hello@jacks.com.co', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          _subject: `New message from ${formData.name}`,
        })
      });

      if (response.ok) {
        setStatus({ submitting: false, submitted: true, error: null });
        setFormData({ name: '', email: '', message: '' });
        setShowSuccessPopup(true);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setStatus({ submitting: false, submitted: false, error: error.message });
      setTimeout(() => setStatus(prev => ({ ...prev, error: null })), 5000);
    }
  };

  return (
    <section id="contact-us" className="relative bg-black py-40">
      {/* Background text */}
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.02] select-none pointer-events-none overflow-hidden">
        <h1 className="text-[20vw] font-bold text-white whitespace-nowrap animate-slide">CONTACT US</h1>
      </div>

      <div className="container relative mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section title */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-24 text-center relative"
          >
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
            <h2 className="text-4xl md:text-6xl font-light text-white mb-6">
              Get in touch
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Our team is here to help you with any questions you may have
            </p>
            <div className="h-px w-16 bg-white/20 mx-auto mt-8"></div>
          </motion.div>

          {/* Main content */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            {/* Left side - Contact info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-16"
            >
              <div className="relative">
                <div className="absolute -left-4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
                
                {/* Contact details */}
                <div className="space-y-12">
                  <motion.div 
                    className="space-y-2 group"
                    whileHover={{ opacity: 0.7 }}
                    transition={{ duration: 0.2 }}
                  >
                    <p className="text-white/40 uppercase tracking-wider text-sm group-hover:text-white/60 transition-colors">Email</p>
                    <a href="mailto:concierge@jacks.com.co" className="text-white text-xl transition-opacity block">
                      concierge@jacks.com.co
                    </a>
                  </motion.div>
                </div>

                {/* Social links */}
                <div className="mt-20 space-y-6">
                  <p className="text-white/40 uppercase tracking-wider text-sm">Follow us</p>
                  <div className="flex space-x-6">
                    {socialLinks.map(({ icon: Icon, href, label }) => (
                      <motion.a
                        key={label}
                        href={href}
                        whileHover={{ y: -5, scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        className="w-12 h-12 rounded-full border-2 border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white transition-colors group"
                        aria-label={label}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="sr-only">{label}</span>
                      </motion.a>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right side - Contact form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute -left-10 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
              
              <form onSubmit={handleSubmit} className="space-y-10">
                <div className="relative group">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-transparent border-b border-white/20 py-4 text-white placeholder-white/40 focus:outline-none focus:border-white transition-colors peer"
                    required
                  />
                  <div className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-300 peer-focus:w-full"></div>
                  <div className="absolute top-0 right-0 text-white/20 text-sm uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">Requis</div>
                </div>

                <div className="relative group">
                  <input
                    type="email"
                    placeholder="Your email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full bg-transparent border-b border-white/20 py-4 text-white placeholder-white/40 focus:outline-none focus:border-white transition-colors peer"
                    required
                  />
                  <div className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-300 peer-focus:w-full"></div>
                  <div className="absolute top-0 right-0 text-white/20 text-sm uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">Requis</div>
                </div>

                <div className="relative group">
                  <textarea
                    placeholder="Your message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full bg-transparent border-b border-white/20 py-4 text-white placeholder-white/40 focus:outline-none focus:border-white transition-colors resize-none h-32 peer"
                    required
                  />
                  <div className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-300 peer-focus:w-full"></div>
                  <div className="absolute top-0 right-0 text-white/20 text-sm uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">Requis</div>
                </div>

                <div className="relative">
                  <motion.button
                    type="submit"
                    disabled={status.submitting}
                    className={`w-full py-4 bg-white text-black rounded-md transition-all font-medium
                      ${status.submitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                    `}
                  >
                    {status.submitting ? 'SENDING...' : 'SEND MESSAGE'}
                  </motion.button>

                  {status.error && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute -bottom-8 left-0 right-0 text-center text-red-500 text-sm"
                    >
                      {status.error}
                    </motion.div>
                  )}
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showSuccessPopup && (
          <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Contact;